import React, { useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, IconButton, TableSortLabel } from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { useReporting } from '../ReportingContext';
import BlogFilters from './BlogFilters';

// ── Blog Stats ──────────────────────────────────────────────────────────────
// Per blog: HEAD revenue (this blog's own traffic on its own pages, broken out
// by page) + SNAKE revenue (this blog's traffic converting on downstream snake
// pages) + blog TOTAL (head + snake). Each row shows the NAMED affiliate's own
// share — head 70% (HEAD_TRAFFIC), snake 40% (SNAKE_TRAFFIC); the snake content
// 30% belongs to a different affiliate and appears in the Snake Stats report.
// RPV = Aff Rev / Views.

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pctv = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(2)}%` : '0.00%');
const rpvv = (aff, views) => `$${views > 0 ? (aff / views).toFixed(2) : '0.00'}`;
const trunc = (v, n) => { const s = v == null ? '' : String(v); return s.length > n ? s.slice(0, n) : s; };

// 9 numeric metric cells shared by every row type.
const MetricCells = ({ m, bold }) => {
  const fw = bold ? 700 : 400;
  const views = m.views || 0, clicks = m.clicks || 0, sales = m.sales || 0;
  return (
    <>
      <TableCell align="right" sx={{ fontWeight: fw }}>{views.toLocaleString()}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{clicks.toLocaleString()}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{pctv(clicks, views)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{sales.toLocaleString()}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{pctv(sales, clicks)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{money(m.totalRevenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{money(m.adminRevenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw, color: '#059669' }}>{money(m.affRevenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{rpvv(m.affRevenue, views)}</TableCell>
    </>
  );
};

// sort holds a key on blog.total (metrics) or blog (identity); derived columns
// (CTR/CVR/RPV, offer/element sub-row fields) are not blog-level sortable.
const COLUMNS = [
  { label: 'Affiliate ID', sort: 'affiliateId' },
  { label: 'Affiliate Name', sort: 'affiliateName' },
  { label: 'Username', sort: 'affiliateUsername' },
  { label: 'Blog ID', sort: 'blogPostId' },
  { label: 'Blog Name', sort: 'blogTitle' },
  { label: 'Offer ID' },
  { label: 'Offer Name' },
  { label: 'Offer Element ID' },
  { label: 'Offer Element Name' },
  { label: 'Views', sort: 'views', align: 'right' },
  { label: 'Clicks', sort: 'clicks', align: 'right' },
  { label: 'CTR', align: 'right' },
  { label: 'Sales', sort: 'sales', align: 'right' },
  { label: 'CVR', align: 'right' },
  { label: 'Total Rev', sort: 'totalRevenue', align: 'right' },
  { label: 'Admin Rev', sort: 'adminRevenue', align: 'right' },
  { label: 'Aff Rev', sort: 'affRevenue', align: 'right' },
  { label: 'RPV', align: 'right' },
];
const METRIC_KEYS = ['views', 'clicks', 'sales', 'totalRevenue', 'adminRevenue', 'affRevenue'];

const BlogStatsTab = () => {
  const { blogStats, blogStatsLoading, blogStatsError } = useReporting();
  const [expanded, setExpanded] = useState({});
  const [sort, setSort] = useState({ key: 'totalRevenue', dir: 'desc' });

  const report = blogStats?.blogReport;

  const blogs = useMemo(() => {
    if (!report?.blogs) return [];
    const arr = [...report.blogs];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let av, bv;
      if (METRIC_KEYS.includes(key)) { av = a.total?.[key] || 0; bv = b.total?.[key] || 0; }
      else { av = a[key]; bv = b[key]; }
      if (typeof av === 'string' || typeof bv === 'string') {
        const r = String(av || '').localeCompare(String(bv || ''), undefined, { numeric: true });
        return dir === 'asc' ? r : -r;
      }
      av = Number(av) || 0; bv = Number(bv) || 0;
      return dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [report, sort]);

  const handleSort = (key) => setSort((p) => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  if (blogStatsLoading) {
    return (<Box><BlogFilters /><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></Box>);
  }
  if (blogStatsError) {
    return (<Box><BlogFilters /><Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography></Box>);
  }
  if (!report || blogs.length === 0) {
    return (
      <Box><BlogFilters />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No blog stats for this period. Blog stats appear once blogs have head or snake traffic in the selected filters.
        </Typography>
      </Box>
    );
  }

  const t = report.totals || {};

  return (
    <Box>
      <BlogFilters />
      <Card sx={{ mb: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Blog Stats</Typography>
            <Typography variant="body2" color="text.secondary">
              Head revenue (own pages, by page) + snake revenue (this blog's traffic on downstream snake pages) + blog total. Click a page-expand to see the head breakdown.
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 40 }} />
                  {COLUMNS.map((c) => (
                    <TableCell key={c.label} align={c.align || 'left'} sx={{ fontWeight: 600 }}>
                      {c.sort ? (
                        <TableSortLabel active={sort.key === c.sort} direction={sort.key === c.sort ? sort.dir : 'desc'} onClick={() => handleSort(c.sort)}>
                          {c.label}
                        </TableSortLabel>
                      ) : c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Grand summary: Total / Head / Snake */}
                <TableRow sx={{ bgcolor: '#fde7cf' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell colSpan={8} />
                  <MetricCells m={t.total || {}} bold />
                </TableRow>
                <TableRow sx={{ bgcolor: '#fdf3e2' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600 }}>Total Head Revenue</TableCell>
                  <TableCell colSpan={8} />
                  <MetricCells m={t.head || {}} />
                </TableRow>
                <TableRow sx={{ bgcolor: '#fdf3e2' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600 }}>Total Snake Revenue</TableCell>
                  <TableCell colSpan={8} />
                  <MetricCells m={t.snake || {}} />
                </TableRow>

                {blogs.map((blog) => {
                  const isOpen = !!expanded[blog.blogPostId];
                  const pages = blog.pages || [];
                  return (
                    <React.Fragment key={blog.blogPostId}>
                      {/* Blog total row (head + snake) */}
                      <TableRow hover onClick={() => toggle(blog.blogPostId)} sx={{ cursor: 'pointer', bgcolor: '#eef5f0' }}>
                        <TableCell padding="checkbox">
                          <IconButton size="small">
                            {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} title={blog.affiliateId || ''}>{blog.affiliateDisplayId != null ? blog.affiliateDisplayId : (blog.affiliateId ? trunc(blog.affiliateId, 12) : '—')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{blog.affiliateName || '—'}</TableCell>
                        <TableCell title={blog.affiliateUsername || ''}>{trunc(blog.affiliateUsername, 12)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{blog.blogPostId}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} title={blog.blogTitle || ''}>{blog.blogTitle ? trunc(blog.blogTitle, 50) : '—'}</TableCell>
                        <TableCell colSpan={4} />
                        <MetricCells m={blog.total || {}} bold />
                      </TableRow>

                      {isOpen && (
                        <>
                          {/* Total head subtotal */}
                          <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell colSpan={4} />
                            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Total head</TableCell>
                            <TableCell colSpan={4} />
                            <MetricCells m={blog.head || {}} />
                          </TableRow>

                          {/* Head pages */}
                          {pages.map((p, idx) => (
                            <TableRow key={`${blog.blogPostId}-p-${idx}`}>
                              <TableCell padding="checkbox" />
                              <TableCell colSpan={4} />
                              <TableCell sx={{ color: 'text.secondary' }}>{p.page != null ? `Page ${p.page}` : 'Page'}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }} title={p.offerId || ''}>{p.offerDisplayId != null ? p.offerDisplayId : (p.offerId || 'n/a')}</TableCell>
                              <TableCell title={p.offerName || ''}>{p.offerName ? trunc(p.offerName, 30) : 'content'}</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>{p.elementId || ''}</TableCell>
                              <TableCell title={p.elementName || ''}>{p.elementName ? trunc(p.elementName, 30) : ''}</TableCell>
                              <MetricCells m={p} />
                            </TableRow>
                          ))}

                          {/* Snake subtotal */}
                          <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell colSpan={4} />
                            <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Snake</TableCell>
                            <TableCell colSpan={4} />
                            <MetricCells m={blog.snake || {}} />
                          </TableRow>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BlogStatsTab;
